import {
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  Box,
  Flex,
  useBoolean,
  MenuButtonProps,
  Text,
  Tooltip,
} from "@chakra-ui/react";
import { useCallback, useMemo, useRef, useState } from "react";
import Search from "./ui/Search";
import EmptyData from "./EmptyData";
import { IconBase } from "./base";
import VirtualScroll from "./VirtualScroll";

interface Option {
  value?: any;
  label?: any;
  title?: string;
  isDisabled?: boolean;
}

export interface SelectorProps extends MenuButtonProps {
  options: Option[];
  value?: any;
  hideSearch?: boolean;
  isDisabled?: boolean;
  listHeight?: number;
  rowHeight?: number;
  onChange(value?: any): void;
}

const Selector: React.FC<SelectorProps> = ({
  options,
  value,
  onChange,
  isDisabled,
  hideSearch = false,
  listHeight = 300, // px
  rowHeight = 36, // px
  placeholder,
  ...other
}) => {
  const [search, setSearch] = useState("");
  const [isOpen, open] = useBoolean();
  const mapItem = useMemo(
    () => new Map(options.map((i) => [i.value, i.label])),
    [options]
  );

  const items = useMemo(() => {
    if (!search) return options;

    return options.filter((option) => {
      return option?.label?.toLowerCase().trim().includes(search);
    });
  }, [search, options]);

  const ref = useRef<any>();

  const handleClickItem = useCallback(
    (value: any) => {
      onChange(value);
      open.off();
    },
    [onChange, open]
  );

  const maxH = Math.min(rowHeight * items.length, listHeight);

  return (
    <Menu
      matchWidth
      isOpen={isOpen}
      onOpen={() => open.on()}
      onClose={() => open.off()}
    >
      <MenuButton
        w="100%"
        borderColor={"transparent"}
        px={0}
        display={"flex"}
        position={"relative"}
        ref={ref}
        disabled={isDisabled}
        cursor={isDisabled ? "not-allowed" : "pointer"}
        borderWidth={0}
        pr="3rem"
        border={"1px solid #E2E2E3"}
        bg="#fff"
        pl="1rem"
        h="4rem"
        whiteSpace={"nowrap"}
        textOverflow={"ellipsis"}
        overflow={"hidden"}
        justifyContent={"flex-start"}
        textAlign={"left"}
        {...other}
      >
        <Tooltip placement="top-start" label={mapItem.get(value)}>
          <Text
            whiteSpace={"nowrap"}
            textOverflow={"ellipsis"}
            overflow={"hidden"}
          >
            {mapItem.get(value) ?? placeholder}
          </Text>
        </Tooltip>
        <Flex
          position={"absolute"}
          right={0}
          top={0}
          h="100%"
          alignItems={"center"}
          px="0.5rem"
        >
          <IconBase
            transformOrigin={"center center"}
            transition={"all .3s ease-in-out"}
            transform={isOpen ? `rotate(180deg)` : ""}
            w="2rem"
            icon="/img/icon-arrow-down-small.svg"
          />
        </Flex>
      </MenuButton>
      {isOpen && (
        <MenuList
          py="0"
          borderRadius={"0.5rem"}
          overflow={"hidden"}
          border={"1px solid #D4D4D499"}
        >
          {!hideSearch && (
            <Box
              borderBottom={"1px solid #D4D4D499"}
              sx={{
                input: {
                  borderColor: "transparent",
                  borderRadius: "0.5rem 0.5rem 0 0",
                  bg: "#fafafa",
                },
                ".search-icon svg": {
                  w: "1.75rem",
                },
              }}
            >
              <Search
                inputProps={{ placeholder: "検索" }}
                isShowSearchIcon
                onSearch={setSearch}
              />
            </Box>
          )}
          <Box h={`${maxH}px`} overflowX={"hidden"} overflowY={"auto"}>
            {items.length ? (
              <VirtualScroll
                items={items}
                rowHeight={rowHeight}
                renderItem={(option) => {
                  const isDisabled =
                    option.value === value || option?.isDisabled;

                  return (
                    <Flex
                      h="4rem"
                      w="100%"
                      alignItems={"center"}
                      borderBottom={"1px solid #D4D4D499"}
                      key={option.value}
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        if (isDisabled) return;
                        handleClickItem(option.value);
                      }}
                      opacity={isDisabled ? 0.5 : 1}
                      as="button"
                      title={option.title}
                      px="1rem"
                    >
                      <Tooltip placement="top-start" label={option.label}>
                        <Box
                          whiteSpace={"nowrap"}
                          textOverflow={"ellipsis"}
                          overflow={"hidden"}
                        >
                          {option.label}
                        </Box>
                      </Tooltip>
                    </Flex>
                  );
                }}
              />
            ) : (
              <Box mx="auto" py="2rem">
                <EmptyData />
              </Box>
            )}
          </Box>
        </MenuList>
      )}
    </Menu>
  );
};

export default Selector;
